const {Component, Fragment} = require("react");
const PropTypes = require("prop-types");
const slotStore = require("stores/HourSlotsStore");
const cartStore = require("stores/CartStore");
const moment = require("moment");
const Date = require("components/forms/Date");
const Select = require("components/forms/Select");
const router = require("services/router");
const {decodeObservable} = require("helpers/object");
const {classList} = require("helpers/html");
const {copyTime} = require("helpers/moment");
const {observer} = require("mobx-react");
const {computed} = require("mobx");
const {animateBodyScrollToAnchorElement} = require("helpers/scroll");
const Load = require("components/mixins/Load");

@observer
class BookingBuilder extends Component {

	constructor(props) {
		super(props);
		this.state = {
			loading: false,
			loadingSlots: []
		};
	}

	get className() {
		return classList(
			"available-times",
			this.state.loading ? "loading" : null
		);
	}

	get dateTime() {
		return slotStore.constraints.after ? moment(slotStore.constraints.after) : moment();
	}

	get hourStart() {
		const dt = this.dateTime;
		const now = moment().add(1, "hour");
		const start = dt.clone().hour(6).minute(0).second(0).millisecond(0);

		return start.isSameOrAfter(now) ? start : now;
	}

	get hourOptions() {
		const start = this.hourStart;
		const current = start.clone().minute(0).second(0).millisecond(0);
		const options = [];

		while (current.day() === start.day()) {
			options.push({
				value: current.format("HH"),
				label: `Efter klokken ${current.format("HH")}`
			});

			current.add(4, "hour");
		}

		return options;
	}

	@computed
	get availableBookings() {
        const now = moment();
		return slotStore.collection.filter(booking => booking.available === true).filter(booking => moment(booking.from).isAfter(now));
	}

	get hasContent() {
		return slotStore.collection.length > 0;
	}

	get humanizedSlotCount() {
		return this.availableBookings.length > 0 ?
			this.availableBookings.length :
			"ikke";
	}

	setLoadingState(loading = true) {
		this.setState({loading});
	}

	load(date) {
		this.setLoadingState();

		slotStore.constrain({after: date.format("YYYY-MM-DD HH:mm:ss")})
			.then(() => {
				this.setLoadingState(false);
				animateBodyScrollToAnchorElement(this.bodyElement, -100);
			})
			.catch(() => {
				this.setLoadingState(false);
			});
	}

	onDateChange = (value) => {
		const date = moment(value);
		const now = moment();

		// If on same day limit time to now + 1h
		if (date.format("YYYY-MM-DD") === now.format("YYYY-MM-DD")) {
			copyTime(date, now).add(1, "hour").minute(0).second(0).millisecond(0);
		}
		// Set time at start of day if any other date
		else {
			date.startOf("day").hour(6).minute(0).second(0).millisecond(0);
		}

		this.load(date);
	};

	onTimeChange = (e) => {
		const value = e.target.value;
		this.load(this.dateTime.clone().hour(parseInt(value)));
	};

	onPaginationNext = (e) => {
		e.preventDefault();
		e.stopPropagation();

		this.onDateChange(this.dateTime.add(1, "day").format("YYYY-MM-DD"));
	};

    onPaginationNextAvailable = (e) => {
		e.preventDefault();
		e.stopPropagation();

		slotStore.getNextAvailableDate()
			.then(date => {
				if(date === null){
					date = this.dateTime;
				}
                this.onDateChange(date);
			})
			.catch(err => {
				console.log(err);
			});

	};

	onPaginationPrev = (e) => {
		e.preventDefault();
		e.stopPropagation();

		// Skip if today
		if (moment().format("YYYY-MM-DD") === this.dateTime.format("YYYY-MM-DD")) {
			return;
		}

		this.onDateChange(this.dateTime.subtract(1, "day").format("YYYY-MM-DD"));
	};

	setSlotLoadingState(slot, loading = true) {
		const index = this.state.loadingSlots.indexOf(slot.from);

		if (loading) {
			if (index === -1) {
				this.setState(state => {
					state.loadingSlots.push(slot.from);
					return state;
				});
			}
		}
		else {
			if (index !== -1) {
				this.setState(state => {
					state.loadingSlots.splice(index, 1);
					return state;
				});
			}
		}
	}

	toggleSlotListener(slot) {
		return (e) => {
			e.preventDefault();
			e.stopPropagation();

			// Skip if slot is unavailable
			if (!slot.available) {
				return;
			}

			this.setSlotLoadingState(slot, true);
			const stopLoadingState = () => this.setSlotLoadingState(slot, false);

			const booking = cartStore.bookingAt(slot.from);

			if (booking) {
				return cartStore.remove(booking.id).then(stopLoadingState).catch(stopLoadingState);
			}

			cartStore.add(slot.from).then(stopLoadingState).catch(() => {
				stopLoadingState();

				// refresh slots on error (some of them might get reserved before any refresh)
				slotStore.fetch();
			});
		};
	}

	isSelected(from) {
		return cartStore.bookingAt(from) != undefined;
	}

	renderFilters() {
		return (
			<div className="available-times-filter animate slide-in">
				<div className="available-times-fields">

					<Date label="Vælg dato" min={moment().toISOString()} value={this.dateTime.format("YYYY-MM-DD")}
								handleChange={this.onDateChange}>
						<img src={router.url("img/icons/choose-date.svg")} alt="Choose date"/>
					</Date>

					<Select label="Vælg fra tidspunkt" options={this.hourOptions} value={this.dateTime.format("HH")}
									onChange={this.onTimeChange}>
						<img src={router.url("img/icons/clock.svg")} alt="Choose time"/>
					</Select>

				</div>
				<p>Vi fandt {this.humanizedSlotCount} {(this.humanizedSlotCount === 1) ? 'ledig tid' : 'ledige tider'}
					<span> d. {this.dateTime.format("DD MMMM YYYY")} efter klokken {this.dateTime.format("HH")}</span>
				</p>
			</div>
		);
	}

	renderSlots() {
		const now = moment();

		return decodeObservable(slotStore.collection)
			.filter(slot => moment(slot.from).isAfter(now))
			.map(slot => {
				const from = moment(slot.from);
				const to = moment(slot.to);
				const selected = this.isSelected(from);
				const available = slot.available;
				const className = classList(
					"available-time",
					available ? null : "disabled",
					selected ? "selected" : null,
					slot.instant && "instant",
					this.state.loadingSlots.indexOf(slot.from) !== -1 ? "loading" : null
				);

				const instantMarkup = slot.instant ? (<p className="instant-booking">Øjeblikkelig booking</p>) : null;

				return (
					<div key={slot.from} className={className} onClick={this.toggleSlotListener(slot)}>
						<div className="available-time-content">
							{instantMarkup}
							<p className="time">{from.format("HH:mm")} - {to.format("HH:mm")}</p>
							<p className="date">{from.format("ddd. DD MMMM")}</p>
						</div>
					</div>
				);
			});
	}

	renderPagination(hide = false) {
		const isToday = moment().format("YYYY-MM-DD") === this.dateTime.format("YYYY-MM-DD");

		let buttons = [];

		buttons.push(
            <button key={"prev"} type="button" className="btn prev" disabled={isToday} onClick={this.onPaginationPrev}>Forrige dag</button>
		);

        if (this.availableBookings.length === 0) {
            buttons.push(
                <button key={"next_available"} type="button" className="btn next" onClick={this.onPaginationNextAvailable}>Næste ledige tid</button>
            );
        }

		buttons.push(
			<button key={"next"} type="button" className="btn next" onClick={this.onPaginationNext}>Næste dag</button>
		);

		return (
			<div className={`pagination animate slide-in ${hide ? 'hidden' : ''}`}>
				<Fragment>{buttons}</Fragment>
			</div>
		);
	}

	render() {
		return (
			<section className={this.className} ref={el => this.bodyElement = el}>
				<div className="available-times-container">
					<div className="available-times-inner">
						<Load></Load>
						{this.renderFilters()}
						<div className="pagination-special">
							{this.renderPagination()}
						</div>
						<div className="all-times animate slide-in">
							{this.renderSlots()}
						</div>
						{this.renderPagination(! this.hasContent)}
					</div>
				</div>
			</section>
		);
	}

}

module.exports = BookingBuilder;



// WEBPACK FOOTER //
// ./resources/js/components/checkout/BookingBuilder.js